import React, { PropsWithChildren } from 'react';

import { red } from '@mui/material/colors';
import { ThemeProvider, createTheme } from '@mui/material/styles';
//  We need to import this file in order for the augmentation to work so that we can override MUI component styles with our custom theme variables.
import type {} from '@mui/x-date-pickers/themeAugmentation';

export const stl_gold_color = '#ffda6a';
export const stl_gold_color_transparent = '#ffda6a80';
export const stl_gold_color_light = '#F0CF7F';
export const stl_blue_color = '#77b1d9'; // blue
export const stl_realtime_color = '#4d8335'; // green
export const stl_realtime_color_light = '#6bad61'; // lighter green, readable against b/w
export const stl_realtime_color_light_desat = '#769375'; // lighter green, readable against b/w
export const stl_background_color = '#252424';
export const stl_secondary_background_color = '#171717';
export const stl_hilited_background_color = '#686868';
export const stl_secondary_hilited_background_color = '#707070';
export const stl_error_color = '#FF9797';
export const background_input = '#313131';
export const background_input_dark = '#101010';
export const background_display_dark = 'rgba(16, 16, 16, 0.50)';
export const stl_hilited_text_color = '#F0CF7F';
export const stl_light_background_color = '#888888';
export const stl_light_grey = '#BBBBBB';
export const white = '#FFFFFF';
export const black = '#000000';
export const primary_text_color = white;
export const secondary_text_color = '#B0B0B0';
export const secondary_light_text_color = '#F5F5F5';
export const secondary_light_transparent_color = 'rgba(245, 245, 245, 0.5)';
export const null_grey = '#7d7d7d'; // From the map
export const transparent_null_grey = 'rgba(125,125,125,0)';
export const semi_transparent_null_grey = 'rgba(125,125,125,0.5)';
export const background_modal = '#292929';
export const stl_new_background = '#202020';
export const background_gray = '#303030';
export const background_gray_light = '#3B3B3B';
export const background_gray_light_2 = '#444';
export const positive_trend = '#b4c94e';
export const negative_trend = '#e16136';
export const gray_btn_color = '#373737';
export const gray_btn_hover_color = '#404040';
export const selected_background = '#444444';
export const border_gray_color = '#4d4d4d';
export const light_gray_text = 'rgba(245, 245, 245, 0.9)';
export const dark_gray_text = '#1e1e1e';
export const dimmed_white_color = 'rgba(245, 245, 245, 0.8)';
export const white_25 = 'rgba(245, 245, 245, 0.25)';
export const white_50 = 'rgba(245, 245, 245, 0.05)';
export const header_color = stl_new_background;
export const workspace_color = stl_new_background;
export const text_input_color = stl_new_background;
export const navbar_color = stl_secondary_background_color;
export const taskbar_color = stl_secondary_background_color;
export const chart_background_color = stl_secondary_background_color;
export const hover_background_color = 'rgba(255, 255, 255, 0.05)';
// https://mui.com/material-ui/customization/default-theme/
// https://mui.com/material-ui/customization/typography/

declare module '@mui/material/styles' {
  interface Palette {
    errorStl: Palette['primary'];
  }
  interface PaletteOptions {
    errorStl?: PaletteOptions['primary'];
  }
}

// A custom theme for this app
const theme = createTheme({
  typography: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
  },
  palette: {
    mode: 'dark',
    primary: {
      main: stl_gold_color_light,
      contrastText: stl_secondary_background_color,
    },
    secondary: {
      main: stl_secondary_background_color,
      contrastText: stl_gold_color_light,
    },
    error: {
      main: red.A400,
    },
    errorStl: {
      main: stl_error_color,
    },
    text: {
      primary: primary_text_color,
      secondary: stl_gold_color,
    },
    background: {
      default: stl_new_background,
    },
  },

  components: {
    MuiIcon: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Font Awesome icons weren't designed like the Material Icons. The fa icons are cropped to use all the space available. We can adjust for this with a global override.
        '.svg-inline--fa': {
          padding: 4,
          width: '1em',
          color: '#fff',
        },
        '[type="button"] *, [role="button"] *, h1, h2, h3, h4, h5, h6': {
          fontFamily: 'var(--font-display)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: '22px',
          textAlign: 'left',
          fontFamily: 'var(--font-display)',
        },
        h2: {
          fontSize: '18px',
          textAlign: 'left',
          fontFamily: 'var(--font-display)',
        },
        h3: {
          fontSize: '16px',
          textAlign: 'left',
          fontFamily: 'var(--font-display)',
        },
        h4: {
          fontSize: '14px',
          textAlign: 'left',
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
        },
        h5: {
          fontSize: '12px',
          textAlign: 'left',
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '& .MuiAccordionSummary-content': {
            flexGrow: 'unset',
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          height: '2rem',
          fontWeight: '500',
          fontFamily: 'var(--font-display)',
        },
        text: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-body)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-display)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-display)',
          backgroundColor: 'rgba(112, 112, 112, 0.30) !important',
          borderColor: 'rgba(112, 112, 112, 0.30)',
          height: '1px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--primary-text)',
        },
      },
    },
    // TextField padding
    MuiFilledInput: {
      styleOverrides: {
        input: {
          paddingTop: '6px',
          paddingBottom: '6px',
          fontSize: '1rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none',
            },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1rem',
          backgroundColor: 'var(--background-col)',
          filter: 'drop-shadow(0px 0px 2px #FFFFFF30)',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: 'var(--font-body)',
        },
        secondary: {
          fontFamily: 'var(--font-body)',
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        disabled: ({ theme }) => ({
          color: theme.palette.text.disabled,
        }),
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        disabled: ({ theme }) => ({
          color: theme.palette.text.disabled,
        }),
      },
    },
  },
});

export function CemThemeProvider({ children }: PropsWithChildren) {
  const vars = `
    :root {
      --background-col: ${stl_new_background};
      --header-color: ${header_color};
      --workspace-background-col: ${workspace_color};
      --navbar-background-color: ${navbar_color};
      --taskbar-background-col: ${taskbar_color};
      --chart_background_color: ${chart_background_color};
      --light-background-col: ${stl_light_background_color};
      --gold-col: ${stl_gold_color};
      --gold-col-light: ${stl_gold_color_light};
      --gold-col-transparent: ${stl_gold_color_transparent};
      --blue-col: ${stl_blue_color};
      --realtime-col: ${stl_realtime_color};
      --light-grey-divider: ${stl_secondary_hilited_background_color};
      --secondary-text: ${secondary_text_color};
      --secondary-light-text: ${secondary_light_text_color};
      --secondary-light-transparent: ${secondary_light_transparent_color};
      --primary-text: ${primary_text_color};
      --highlighted-text: ${stl_hilited_text_color};
      --null-grey: ${null_grey};
      --background-modal: ${background_modal}; 
      --stl-new-background: ${stl_new_background};
      --background-gray: ${background_gray};
      --background-light-gray: ${background_gray_light};
      --background-light-gray-2: ${background_gray_light_2};
      --background-input: ${background_input};
      --background-input-dark: ${background_input_dark};
      --background-display-dark: ${background_display_dark};
      --negative-trend: ${negative_trend};
      --positive-trend: ${positive_trend};
      --text_input_color: ${text_input_color};
      --gray-btn-color: ${gray_btn_color};
      --gray-btn-hover-color: ${gray_btn_hover_color};
      --hover-background: ${hover_background_color};
      --selected-background: ${selected_background};
      --border-gray: ${border_gray_color};
      --light-gray-txt: ${light_gray_text};
      --dark-gray-text: ${dark_gray_text};
      --dimmed-txt: ${dimmed_white_color};
      --white-25: ${white_25};
      --white-50: ${white_50};
      --error-color: ${stl_error_color};
      --light-gray: ${stl_light_grey};
      --stl_realtime_color_light: ${stl_realtime_color_light};
    } 
  `;
  return (
    <>
      <style>{vars}</style>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </>
  );
}

export default theme;
